import React, { useCallback, useMemo, useState } from "react";
import {
  Alert as MuiAlert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  useFetchOrganizationsQuery,
  useFetchOrganizationUsersQuery,
  useAddUsersFromOrgMutation,
} from "../../services/profileService";

const ROLE_TYPE = {
  managers: "manager",
  employees: "employee",
};

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getUserDisplayName = (user) => {
  if (!user || typeof user !== "object") return "—";
  if (user.name) return String(user.name);
  const parts = [user.first_name, user.last_name].filter(Boolean);
  if (parts.length) return parts.join(" ");
  if (user.full_name) return String(user.full_name);
  return "—";
};

/**
 * Modal for adding users from another organization into the current (target) organization.
 * Supports multi-select with email conflict detection — users already assigned to the
 * target org (as manager or employee) cannot be selected.
 */
const AddUsersFromOrgModal = ({ open, onClose, onSuccess, targetOrgId }) => {
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [activeRoleType, setActiveRoleType] = useState(ROLE_TYPE.managers);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  // All organizations for the source org dropdown (exclude the target org itself)
  const { data: orgsPayload, isLoading: orgsLoading } =
    useFetchOrganizationsQuery(undefined, { skip: !open });

  const organizations = useMemo(() => {
    return normalizeArray(orgsPayload).filter((org) => {
      const id = org.id ?? org.organization_id ?? org.organizationID;
      return Number(id) !== Number(targetOrgId);
    });
  }, [orgsPayload, targetOrgId]);

  // Users from the selected source org filtered by the active role tab
  const {
    data: sourceUsersPayload,
    isLoading: sourceUsersLoading,
    isFetching: sourceUsersFetching,
  } = useFetchOrganizationUsersQuery(
    { organizationId: selectedOrgId, roleType: activeRoleType },
    { skip: !selectedOrgId || !open }
  );

  const sourceUsers = useMemo(
    () => normalizeArray(sourceUsersPayload),
    [sourceUsersPayload]
  );

  // Fetch existing managers + employees of the target org for email conflict detection
  const { data: targetManagersPayload } = useFetchOrganizationUsersQuery(
    { organizationId: targetOrgId, roleType: ROLE_TYPE.managers },
    { skip: !targetOrgId || !open }
  );

  const { data: targetEmployeesPayload } = useFetchOrganizationUsersQuery(
    { organizationId: targetOrgId, roleType: ROLE_TYPE.employees },
    { skip: !targetOrgId || !open }
  );

  // Set of lowercase emails already present in the target org (both roles)
  const targetOrgEmailSet = useMemo(() => {
    const managers = normalizeArray(targetManagersPayload);
    const employees = normalizeArray(targetEmployeesPayload);
    return new Set(
      [...managers, ...employees]
        .map((u) => u.email?.toLowerCase())
        .filter(Boolean)
    );
  }, [targetManagersPayload, targetEmployeesPayload]);

  // Build DataGrid rows, flagging users whose email is already in the target org
  const rows = useMemo(
    () =>
      sourceUsers.map((member, index) => {
        const email = member.email ?? "—";
        return {
          id: member.id ?? member.user_id ?? `member-${index}`,
          name: getUserDisplayName(member),
          email,
          alreadyAssigned:
            email !== "—" && targetOrgEmailSet.has(email.toLowerCase()),
        };
      }),
    [sourceUsers, targetOrgEmailSet]
  );

  const [addUsersFromOrg, { isLoading: isSubmitting }] =
    useAddUsersFromOrgMutation();

  const handleTabChange = useCallback((_event, newRoleType) => {
    setActiveRoleType(newRoleType);
    setSelectedUserIds([]);
    setErrorMessage("");
  }, []);

  const handleOrgChange = useCallback((event) => {
    setSelectedOrgId(event.target.value);
    setSelectedUserIds([]);
    setErrorMessage("");
  }, []);

  const resetState = useCallback(() => {
    setSelectedOrgId("");
    setActiveRoleType(ROLE_TYPE.managers);
    setSelectedUserIds([]);
    setErrorMessage("");
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleSubmit = useCallback(async () => {
    if (selectedUserIds.length === 0) {
      setErrorMessage("Please select at least one user to add.");
      return;
    }

    setErrorMessage("");
    try {
      await addUsersFromOrg({
        targetOrgId,
        data: {
          user_ids: selectedUserIds.map(Number),
          role: activeRoleType,
          source_organization_id: Number(selectedOrgId),
          is_owner: false,
        },
      }).unwrap();

      const count = selectedUserIds.length;
      const roleLabel = activeRoleType === ROLE_TYPE.managers ? "manager" : "employee";
      resetState();
      onClose();
      onSuccess?.(
        `${count} ${roleLabel}${count !== 1 ? "s" : ""} added successfully to the organization.`
      );
    } catch (err) {
      const msg =
        err?.data?.message ??
        err?.data?.detail ??
        err?.message ??
        "Failed to add users. Please try again.";
      setErrorMessage(String(msg));
    }
  }, [
    addUsersFromOrg,
    targetOrgId,
    selectedUserIds,
    activeRoleType,
    selectedOrgId,
    resetState,
    onClose,
    onSuccess,
  ]);

  const tabLabel =
    activeRoleType === ROLE_TYPE.managers ? "Managers" : "Employees";
  const isUsersLoading = sourceUsersLoading || sourceUsersFetching;

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", minWidth: 80 },
      { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
      {
        field: "status",
        headerName: "Status",
        minWidth: 160,
        sortable: false,
        renderCell: (params) =>
          params.row.alreadyAssigned ? (
            <Tooltip title="This email is already assigned to the target organization">
              <AlreadyAssignedBadge>Already Assigned</AlreadyAssignedBadge>
            </Tooltip>
          ) : null,
      },
    ],
    []
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      sx={{ "& .MuiDialog-paper": { borderRadius: "10px" } }}
    >
      <ModalTitleWrap>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "20px", color: "#000" }}>
            Add Users from Another Organization
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
            Select a source organization, then choose users to add.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Close modal">
          <CloseIcon />
        </IconButton>
      </ModalTitleWrap>

      <DialogContent sx={{ p: 3 }}>
        {errorMessage && (
          <MuiAlert
            severity="error"
            sx={{ mb: 2, borderRadius: "7px" }}
            onClose={() => setErrorMessage("")}
          >
            {errorMessage}
          </MuiAlert>
        )}

        {/* Source organization selector */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="source-org-select-label">
            Select Source Organization
          </InputLabel>
          <OrgSelect
            labelId="source-org-select-label"
            value={selectedOrgId}
            label="Select Source Organization"
            onChange={handleOrgChange}
            disabled={orgsLoading}
            displayEmpty
          >
            {orgsLoading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
              </MenuItem>
            ) : organizations.length === 0 ? (
              <MenuItem disabled>No other organizations found</MenuItem>
            ) : (
              organizations.map((org) => {
                const orgId =
                  org.id ?? org.organization_id ?? org.organizationID;
                const orgName =
                  org.organization_name ?? org.name ?? org.organizationName;
                return (
                  <MenuItem key={orgId} value={orgId}>
                    {orgName ?? `Organization ${orgId}`}
                  </MenuItem>
                );
              })
            )}
          </OrgSelect>
        </FormControl>

        {/* Role type tabs + user list (visible only after org selection) */}
        {selectedOrgId && (
          <>
            <TabsWrap
              value={activeRoleType}
              onChange={handleTabChange}
              aria-label="Select role type"
              sx={{ mb: 2 }}
            >
              <Tab label="Managers" value={ROLE_TYPE.managers} />
              <Tab label="Employees" value={ROLE_TYPE.employees} />
            </TabsWrap>

            {isUsersLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  py: 4,
                }}
              >
                <CircularProgress size={28} />
                <Typography color="text.secondary">
                  Loading {tabLabel.toLowerCase()}…
                </Typography>
              </Box>
            ) : (
              <>
                {selectedUserIds.length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, color: "#1085AB", fontWeight: 500 }}
                  >
                    {selectedUserIds.length} user
                    {selectedUserIds.length !== 1 ? "s" : ""} selected
                  </Typography>
                )}
                <DataGrid
                  rows={rows}
                  columns={columns}
                  checkboxSelection
                  isRowSelectable={(params) => !params.row.alreadyAssigned}
                  rowSelectionModel={selectedUserIds}
                  onRowSelectionModelChange={(newSelection) =>
                    setSelectedUserIds(newSelection)
                  }
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  pageSizeOptions={[10, 30]}
                  disableColumnMenu
                  autoHeight
                  localeText={{
                    noRowsLabel: `No ${tabLabel.toLowerCase()} found in this organization`,
                  }}
                  getRowClassName={(params) =>
                    params.row.alreadyAssigned ? "row--already-assigned" : ""
                  }
                  sx={{
                    "& .row--already-assigned": {
                      opacity: 0.5,
                      pointerEvents: "none",
                    },
                    "& .MuiDataGrid-iconButtonContainer": {
                      visibility: "visible !important",
                      width: "auto !important",
                      svg: { opacity: "1 !important" },
                    },
                  }}
                />
              </>
            )}
          </>
        )}

        {!selectedOrgId && !orgsLoading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            Please select a source organization to view its managers and
            employees.
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, pb: 3, pt: 0, gap: 1, justifyContent: "flex-end" }}
      >
        <CancelBtn onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </CancelBtn>
        <SubmitBtn
          onClick={handleSubmit}
          disabled={
            !selectedOrgId || selectedUserIds.length === 0 || isSubmitting
          }
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PersonAddIcon fontSize="small" />
            )
          }
        >
          {isSubmitting ? "Adding…" : "OK"}
        </SubmitBtn>
      </DialogActions>
    </Dialog>
  );
};

export default AddUsersFromOrgModal;

const ModalTitleWrap = styled(DialogTitle)(() => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  padding: "20px 24px 16px",
  borderBottom: "1px solid #E5E7EB",
}));

const OrgSelect = styled(Select)(() => ({
  borderRadius: "7px",
  background: "#ffffff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#DADADA",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1085AB",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1085AB",
  },
}));

const TabsWrap = styled(Tabs)(() => ({
  minHeight: "42px",
  borderBottom: "1px solid #E5E7EB",
  "& .MuiTab-root": {
    textTransform: "capitalize",
    fontSize: "16px",
    fontWeight: "500",
    color: "#6B7280",
    minHeight: "42px",
  },
  "& .Mui-selected": {
    color: "#1085AB !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#1085AB",
  },
}));

const AlreadyAssignedBadge = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  fontSize: "11px",
  fontWeight: 500,
  color: "#9CA3AF",
  background: "#F3F4F6",
  borderRadius: "4px",
  padding: "2px 8px",
  border: "1px solid #E5E7EB",
  cursor: "default",
}));

const CancelBtn = styled(Button)(() => ({
  minWidth: "auto",
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#2C2C2E",
  textTransform: "capitalize",
  background: "#ffffff",
  borderRadius: "5px",
  padding: "10px 16px",
  boxShadow: "none !important",
  border: "1px solid #DADADA",
  "&:hover": {
    background: "#f5f5f5",
    borderColor: "#DADADA",
  },
}));

const SubmitBtn = styled(Button)(() => ({
  minWidth: "auto",
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#1085AB",
  borderColor: "#1085AB",
  borderRadius: "5px",
  padding: "10px 16px",
  boxShadow: "none !important",
  "&:hover": {
    background: "#0c6d8e",
    borderColor: "#0c6d8e",
  },
  "&.Mui-disabled": {
    background: "#B0BEC5",
    color: "#ffffff",
  },
}));
